import '../../components/ui/dialog-layout.css';
/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useRef, useState, useEffect } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogContent, DialogFooter, DialogBody, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  XMarkIcon,
  ArrowsRightLeftIcon,
  PlusIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/solid";

const NETSUITE_DROPDOWN_OPTIONS = [
  { label: "Program", value: "Program" },
  { label: "Region", value: "Region" },
];

const BLOOMERANG_DROPDOWN_OPTIONS = [
  { label: "Campaign", value: "Campaign" },
  { label: "Funds", value: "Funds" },
  { label: "Appeal", value: "Appeal" },
];

const VIRTUOUS_DROPDOWN_OPTIONS = [{ label: "Project", value: "Project" }];

export default function FieldMapperModal({
  open,
  setOpen,
  integrationType,
  funds,
  productGroups,
  customInputs,
  originalMappings,
  setOriginalMappings,
  bloomerangAppeals = [],
  bloomerangCampaigns = [],
  bloomerangFunds = [],
  virtuousProjects = [],
}) {
  const cancelButtonRef = useRef(null);
  const [mappings, setMappings] = useState([
    {
      charityStackLabel: "",
      charityStackValue: "",
      integrationLabel: "",
      integrationValue: "",
      integrationType: integrationType,
    },
  ]);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (open) {
      setMappings(
        originalMappings ? JSON.parse(JSON.stringify(originalMappings)) : []
      );
    } else {
      setMappings([]);
    }
  }, [open]);

  const addField = () => {
    setMappings([
      ...mappings,
      {
        charityStackLabel: "",
        charityStackValue: "",
        integrationLabel: "",
        integrationValue: "",
        integrationType: integrationType,
      },
    ]);
  };

  const removeField = (index) => {
    const newMappings = [...mappings];
    newMappings.splice(index, 1);
    setMappings(newMappings);
  };

  const updateMapping = (index, field, value, optgroupLabel = null) => {
    const newMappings = [...mappings];
    if (field === "integrationLabel") {
      newMappings[index].integrationLabel = value;
    } else if (field === "integrationValue") {
      newMappings[index].integrationValue = value;
    } else if (field === "charityStack") {
      if (optgroupLabel) {
        newMappings[index].charityStackLabel = optgroupLabel;
        if (
          optgroupLabel !== "Funds" &&
          optgroupLabel !== "Sponsorship Options"
        ) {
          newMappings[index].charityStackLabel = "Custom Inputs";
        }
        if (value.includes(" |DELIMMITER| ")) {
          newMappings[index].charityStackParentValue =
            value.split(" |DELIMMITER| ")[0];
          newMappings[index].charityStackValue =
            value.split(" |DELIMMITER| ")[1];
        } else {
          newMappings[index].charityStackValue = value;
          delete newMappings[index].charityStackParentValue;
        }
      }
    }
    setMappings(newMappings);
  };

  const clearValidationError = (index) => {
    if (validationErrors.length > 0) {
      const newValidationErrors = [...validationErrors];
      if (newValidationErrors[index]) {
        // Clear all validation errors for this mapping
        newValidationErrors[index] = {
          charityStackValue: false,
          integrationLabel: false,
          integrationValue: false,
          isDuplicate: false,
        };
        setValidationErrors(newValidationErrors);
      }
    }
  };

  const truncateText = (text, maxLength = 15) => {
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  let dropdownOptions = [];
  let integrationValuesText = "";

  if (integrationType === "NETSUITE") {
    dropdownOptions = NETSUITE_DROPDOWN_OPTIONS;
    integrationValuesText = "NetSuite Values";
  } else if (integrationType === "BLOOMERANG") {
    dropdownOptions = BLOOMERANG_DROPDOWN_OPTIONS;
    integrationValuesText = "Bloomerang Values";
  } else if (integrationType === "VIRTUOUS") {
    dropdownOptions = VIRTUOUS_DROPDOWN_OPTIONS;
    integrationValuesText = "Virtuous Values";
  }

  const validateMappings = () => {
    // First check for empty required fields
    const errors = mappings.map((mapping) => ({
      charityStackValue: !mapping.charityStackValue,
      integrationLabel: !mapping.integrationLabel,
      integrationValue: !mapping.integrationValue,
      isDuplicate: false, // Add new field for duplicate detection
    }));

    // Check for duplicates
    mappings.forEach((mapping, index) => {
      for (let i = index + 1; i < mappings.length; i++) {
        const otherMapping = mappings[i];

        // Compare all specified fields, treating undefined as equal
        const isDuplicate =
          (mapping.charityStackValue === otherMapping.charityStackValue ||
            (mapping.charityStackValue === undefined &&
              otherMapping.charityStackValue === undefined)) &&
          (mapping.integrationLabel === otherMapping.integrationLabel ||
            (mapping.integrationLabel === undefined &&
              otherMapping.integrationLabel === undefined)) &&
          (mapping.charityStackLabel === otherMapping.charityStackLabel ||
            (mapping.charityStackLabel === undefined &&
              otherMapping.charityStackLabel === undefined)) &&
          (mapping.charityStackParentValue ===
            otherMapping.charityStackParentValue ||
            (mapping.charityStackParentValue === undefined &&
              otherMapping.charityStackParentValue === undefined));

        if (isDuplicate) {
          errors[index].isDuplicate = true;
          errors[i].isDuplicate = true;
        }
      }
    });

    setValidationErrors(errors);
    return errors.every(
      (error) =>
        !error.charityStackValue &&
        !error.integrationLabel &&
        !error.integrationValue &&
        !error.isDuplicate
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        showX={false}
        className="integration-mapping-dialog flex flex-col max-w-[500px] max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] overflow-hidden px-[18px] py-[22px]"
      >
        <DialogBody className="space-y-4">
          <div className="gap-[19px] sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-edge bg-paper-inset sm:mx-0 sm:h-10 sm:w-10">
              <ArrowsRightLeftIcon className="size-6 text-ink-muted" />
            </div>

            <div className="mt-0 flex-grow">
              <DialogTitle className="text-lg text-ink-muted">Map Fields</DialogTitle>
              <div className="mt-2">
                <p className="text-sm text-ink-muted">
                  Map your CharityStack fields to match the data structure of your
                  connected platform.
                </p>

                <div className="mt-4 flex flex-col gap-2">
                  <div className="mapping-column-headings flex items-start gap-[57px] self-stretch">
                    <div className="text-base font-medium text-ink-muted">
                      CharityStack Values
                    </div>
                    <div className="text-base font-medium text-ink-muted">
                      {integrationValuesText}
                    </div>
                  </div>

                  {mappings.length > 0 && (
                    <div className="flex flex-col gap-2">
                      {mappings.map((mapping, index) => (
                        <div
                          key={index}
                          className="mapping-field-row flex items-center justify-center gap-2 self-stretch"
                        >
                          <span className="text-xs text-ink-muted sm:hidden">CharityStack Values</span>
                          <select
                            aria-label="CharityStack value"
                            className={`my-auto flex h-[32px] w-[170.5px] shrink-0 items-center justify-between self-stretch rounded-md border ${
                              validationErrors[index]?.charityStackValue ||
                              validationErrors[index]?.isDuplicate
                                ? "border-red-500"
                                : "border-edge"
                            } bg-paper-muted px-[6px] py-0 text-base text-black focus:border-edge focus:outline-none focus:ring-0`}
                            value={mapping.charityStackValue}
                            title={mapping.charityStackValue}
                            onChange={(e) => {
                              const selectedOption =
                                e.target.options[e.target.selectedIndex];
                              const optgroupLabel =
                                selectedOption.parentNode.label || null;
                              updateMapping(
                                index,
                                "charityStack",
                                e.target.value,
                                optgroupLabel
                              );
                            }}
                            onFocus={() => clearValidationError(index)}
                            required
                          >
                            {mapping.charityStackValue ? (
                              <option value={mapping.charityStackValue} disabled>
                                {truncateText(mapping.charityStackValue)}
                              </option>
                            ) : (
                              <option value="" disabled hidden></option>
                            )}

                            {funds && funds.length > 0 && (
                              <optgroup label="Funds">
                                {funds.map((fund) => (
                                  <option key={fund} value={fund}>
                                    {fund}
                                  </option>
                                ))}
                              </optgroup>
                            )}

                            {productGroups && productGroups.length > 0 && (
                              <optgroup label="Sponsorship Options">
                                {productGroups.map(
                                  (group) =>
                                    group.products &&
                                    group.products.map((product) => (
                                      <option
                                        key={`${group.productGroupName} |DELIMMITER| ${product.productName}`}
                                        value={`${group.productGroupName} |DELIMMITER| ${product.productName}`}
                                      >
                                        {product.productName}
                                      </option>
                                    ))
                                )}
                              </optgroup>
                            )}

                            {customInputs.map((input) => {
                              if (input.type === "SINGLE_SELECTION_DROPDOWN") {
                                return (
                                  <optgroup key={`optgroup-dropdown-${input.title}`} label={input.title}>
                                    {input.options.map((option) => (
                                      <option
                                        key={`${input.title} |DELIMMITER| ${option}`}
                                        value={`${input.title} |DELIMMITER| ${option}`}
                                      >
                                        {option}
                                      </option>
                                    ))}
                                  </optgroup>
                                );
                              } else if (
                                input.type === "SHORT_TEXT_INPUT" ||
                                input.type === "LONG_TEXT_INPUT"
                              ) {
                                return (
                                  <optgroup key={`optgroup-text-${input.title}`} label={input.title}>
                                    <option
                                      key={`${input.title} |DELIMMITER| Input Value`}
                                      value={`${input.title} |DELIMMITER| Input Value`}
                                    >
                                      Input Value
                                    </option>
                                  </optgroup>
                                );
                              }
                            })}
                          </select>

                          <div className="mapping-direction flex aspect-square h-[18px] w-[18px] text-ink-secondary">
                            <ArrowRightIcon />
                          </div>

                          <div className="flex w-[170.5px] flex-col items-start gap-1 self-stretch">
                            <span className="text-xs text-ink-muted sm:hidden">{integrationValuesText}</span>
                            <select
                              className={`flex h-[32px] items-center justify-between self-stretch rounded-md border ${
                                validationErrors[index]?.integrationLabel ||
                                validationErrors[index]?.isDuplicate
                                  ? "border-red-500"
                                  : "border-edge"
                              } bg-paper-muted px-[6px] py-0 text-base text-black focus:border-edge focus:outline-none focus:ring-0`}
                              aria-label="Integration field"
                              value={mapping.integrationLabel}
                              onChange={(e) =>
                                updateMapping(
                                  index,
                                  "integrationLabel",
                                  e.target.value
                                )
                              }
                              onFocus={() => clearValidationError(index)}
                              required
                            >
                              {mapping.integrationLabel ? (
                                <option aria-label="Integration field"
                              value={mapping.integrationLabel} disabled>
                                  {truncateText(mapping.integrationLabel)}
                                </option>
                              ) : (
                                <option value="" disabled hidden></option>
                              )}
                              {dropdownOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>

                            {integrationType === "BLOOMERANG" ? (
                              <select
                                className={`flex h-[32px] items-center justify-between self-stretch rounded-md border ${
                                  validationErrors[index]?.integrationValue ||
                                  validationErrors[index]?.isDuplicate
                                    ? "border-red-500"
                                    : "border-edge"
                                } bg-paper-muted px-[6px] py-0 text-base text-black focus:border-edge focus:outline-none focus:ring-0 sm:text-sm`}
                                style={{
                                  display: "inline-block",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                aria-label="Integration value"
                                value={mapping.integrationValue}
                                onChange={(e) =>
                                  updateMapping(
                                    index,
                                    "integrationValue",
                                    e.target.value
                                  )
                                }
                                onFocus={() => clearValidationError(index)}
                              >
                                <option value="" disabled hidden></option>

                                {mapping.integrationLabel === "Appeal" &&
                                  bloomerangAppeals.map((appeal) => (
                                    <option key={appeal.id} value={appeal.id}>
                                      {appeal.name}
                                    </option>
                                  ))}

                                {mapping.integrationLabel === "Campaign" &&
                                  bloomerangCampaigns.map((campaign) => (
                                    <option key={campaign.id} value={campaign.id}>
                                      {campaign.name}
                                    </option>
                                  ))}
                                {mapping.integrationLabel === "Funds" &&
                                  bloomerangFunds.map((fund) => (
                                    <option key={fund.id} value={fund.id}>
                                      {fund.name}
                                    </option>
                                  ))}
                              </select>
                            ) : integrationType === "VIRTUOUS" ? (
                              <select
                                className={`flex h-[32px] items-center justify-between self-stretch rounded-md border ${
                                  validationErrors[index]?.integrationValue ||
                                  validationErrors[index]?.isDuplicate
                                    ? "border-red-500"
                                    : "border-edge"
                                } bg-paper-muted px-[6px] py-0 text-base text-black focus:border-edge focus:outline-none focus:ring-0 sm:text-sm`}
                                style={{
                                  display: "inline-block",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                aria-label="Integration value"
                                value={mapping.integrationValue}
                                onChange={(e) =>
                                  updateMapping(
                                    index,
                                    "integrationValue",
                                    e.target.value
                                  )
                                }
                                onFocus={() => clearValidationError(index)}
                              >
                                <option value="" disabled hidden></option>

                                {virtuousProjects.map((project) => (
                                  <option key={project.id} value={project.id}>
                                    {project.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                className={`flex h-[32px] items-center justify-between self-stretch rounded-md border ${
                                  validationErrors[index]?.integrationValue ||
                                  validationErrors[index]?.isDuplicate
                                    ? "border-red-500"
                                    : "border-edge"
                                } bg-paper-muted px-[6px] py-0 text-base text-black focus:border-edge focus:outline-none focus:ring-0`}
                                aria-label="Integration value"
                                value={mapping.integrationValue}
                                onChange={(e) =>
                                  updateMapping(
                                    index,
                                    "integrationValue",
                                    e.target.value
                                  )
                                }
                                onFocus={() => clearValidationError(index)}
                              />
                            )}
                          </div>

                          <button
                            className="flex aspect-square h-6 w-6 items-center justify-center text-ink-muted hover:text-ink-secondary"
                            onClick={() => removeField(index)}
                          >
                            <XMarkIcon className="h-6 w-6 text-ink-muted" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className="mt-2 flex items-center justify-end text-base text-sm text-ink-muted hover:text-ink-secondary"
                    onClick={addField}
                  >
                    <PlusIcon className="mr-1 h-6 w-6" />
                    Add Fields
                  </button>
                </div>

                <Separator className="mb-0 mt-4 bg-paper-inset text-edge" />
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="neutral"
            size="workspace"
            onClick={() => setOpen(false)}
            ref={cancelButtonRef}
          >
            Close
          </Button>

          <Button
            type="button"
            variant="confirm"
            size="workspace"
            onClick={() => {
              const isValid = validateMappings();
              if (isValid) {
                setOriginalMappings(JSON.parse(JSON.stringify(mappings)));
                setOpen(false);
                //   props.activateForm && props.activateForm();
              }
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
